// services/api.ts
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://dein-ernaehrungsplan-backend-po2wyl2tv.vercel.app/api';

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  tier: string;
  subscriptionEnd: string;
  hasCompletedIntake: boolean;
  promptCount: number;
  lastLogin: string;
  status: 'active' | 'inactive' | 'banned';
  createdAt: string;
  activeDays: number;
  stripeCustomerId: string;
}

export interface AdminStats {
  totalUsers: number;
  vipUsers: number;
  basicUsers: number;
  activeSubscriptions: number;
  totalRevenue: number;
  recentSignups: number;
  monthlyRevenue: Array<{ month: string; revenue: number; transactions: number }>;
  userGrowth: Array<{ month: string; new_users: number }>;
  planDistribution: Array<{ tier: string; count: number }>;
}

export interface User {
  id: string;
  email: string;
  name: string;
  tier: 'guest' | 'basic' | 'basic-yearly' | 'vip' | 'vip-yearly';
  role?: 'user' | 'admin';
  subscriptionEnd: string;
  hasCompletedIntake: boolean;
  mealPlan?: MealPlan;
  promptCount: number;
  isAuthenticated?: boolean;
  createdAt?: string;
  // Intake form data properties
  age?: string;
  gender?: string;
  height?: string;
  weight?: string;
  activityLevel?: string;
  goal?: string;
  targetWeight?: string;
  timeframe?: string;
  mealsPerDay?: string;
  snacksPerDay?: string;
  dietaryRestrictions?: string[];
  allergies?: string[];
  dislikedFoods?: string[];
  likedFoods?: string[];
  targetCalories?: string;
  cookingTime?: string;
  budget?: string;
}

export interface MealPlan {
  id?: string;
  generatedAt: string;
  days: DayPlan[];
  text: string;
  totalCalories: number;
  shoppingList: string[];
  dietType?: string;
}

export interface DayPlan {
  day: string;
  meals: Meal[];
}

export interface Meal {
  name: string;
  foods: string[];
  calories: number;
  time: string;
}

export interface IntakeFormData {
  age: string;
  gender: string;
  height: string;
  weight: string;
  activityLevel: string;
  goal: string;
  targetWeight: string;
  timeframe: string;
  mealsPerDay: string;
  snacksPerDay: string;
  dietaryRestrictions: string[];
  allergies: string[];
  dislikedFoods: string[];
  likedFoods: string[];
  targetCalories: string;
  cookingTime: string;
  budget: string;
}

export interface UserStats {
  activeDays: number;
  targetProgress: number;
  nextUpdate: string;
}

export interface BillingTransaction {
  id: string;
  amount: number;
  currency: string;
  date: string;
  plan: string;
}

export interface ChatRequest {
  message: string;
  sessionId: string;
  language?: 'de' | 'en';
  intentHint?: string;
}

export interface ExtendedMeal extends Meal {
  recipe?: string;
  ingredients?: string[];
  ingredientsDetailed?: { name: string; amountText?: string; unit?: string; grams?: number; estimatedCostCHF?: number }[];
  preparation?: string;
  nutrition?: { protein?: string; carbs?: string; fat?: string; fiber?: string };
}

export interface ExtendedDayPlan extends DayPlan {
  meals: ExtendedMeal[];
  snacks?: ExtendedMeal[];
}

export interface ExtendedMealPlan extends MealPlan {
  days: ExtendedDayPlan[];
  userInfo?: { name: string; goal?: string; targetCalories?: string };
}

export interface PDFGenerationRequest {
  mealPlan: any;
  user: { id: string; name: string; email: string };
  store?: string;
}

export interface ChatResponse {
  success: boolean;
  response?: string | null;
  message?: string;
  error?: string;
  sessionId?: string;
  promptCount?: number;
  limit?: number;
  isVIP?: boolean;
  requiresUpgrade?: boolean;
  mealPlan?: MealPlan | ExtendedMealPlan;
  isMealPlanFlow?: boolean;
  currentStep?: number;
  missingFields?: string[];
  language?: 'de' | 'en';
}

export interface MealPlanResponse {
  success: boolean;
  mealPlan?: any;
  message?: string;
  error?: string;
}

export interface RefinedQuestionRequest {
  questionType: string;
  userData?: any;
  conversationContext?: any;
  previousAnswer?: string;
  previousAnswers?: string[];
  currentStep?: number;
  totalSteps?: number;
  ensureCompleteness?: boolean;
}

export interface RefinedQuestionResponse {
  refinedQuestion: string;
  options?: string[];
  shouldProceed: boolean;
  message?: string;
}

export interface ValidationRequest {
  conversationData?: Record<string, any>;
}

export interface ValidationResponse {
  isComplete: boolean;
  missingFields?: string[];
  suggestedNextStep?: string;
  errorMessage?: string;
}

export interface AnswerProcessingRequest {
  userAnswer?: string;
  questionType?: string;
  userData?: any;
  conversationContext?: any;
  currentStep?: number;
  totalSteps?: number;
  question?: string;
  field?: string;
  previousData?: any;
  isFinalStep?: boolean;
}

export interface AnswerProcessingResponse {
  isValid: boolean;
  errorMessage?: string;
  processedValue?: any;
  // extended fields used by NutriBot
  collectedData?: Record<string, any>;
  confidence?: number;
  insights?: string | Record<string, any>;
  shouldContinue?: boolean;
  isComplete?: boolean;
}

export interface ValidationRequest {
  // original fields
  collectedAnswers?: any[];
  totalSteps?: number;
  // add conversationData used by NutriBot
  conversationData?: Record<string, any>;
  // currentStep is not supported by backend; remove from usage in caller
}

export interface MealPlanGenerationRequest {
  // original fields
  userInfo?: Record<string, any>;
  // add conversationData used by NutriBot
  conversationData?: Record<string, any>;
}

// Duplicate MealPlanResponse removed to avoid TS2717 conflict. Use the top-level MealPlanResponse.

export interface ValidationResponse {
  isComplete: boolean;
  missingFields?: string[];
  suggestedNextStep?: string;
  errorMessage?: string;
}

export interface AnswerProcessingRequest {
  userAnswer?: string;
  questionType?: string;
  userData?: any;
  conversationContext?: any;
  currentStep?: number;
  totalSteps?: number;
  question?: string;
  field?: string;
  previousData?: any;
  isFinalStep?: boolean;
}

export interface AnswerProcessingResponse {
  isValid: boolean;
  processedValue?: any;
  shouldContinue?: boolean;
  nextStep?: string;
  message?: string;
  errorMessage?: string;
  isComplete?: boolean;
}

// Duplicate MealPlanGenerationRequest removed to avoid TS2717 conflict.

export class ApiService {
  static getUserIdFromToken(): string | null {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    try {
      const payloadJson = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
      const payload = JSON.parse(payloadJson);
      return payload.userId || payload.id || payload.sub || localStorage.getItem('userId') || null;
    } catch {
      return null;
    }
  }
  private static async request(endpoint: string, options: RequestInit = {}): Promise<any> {
    const token = localStorage.getItem('token');

    const uid = ApiService.getUserIdFromToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      // Do NOT attach x-user-id globally to avoid CORS preflight failures
      ...options.headers,
    };

    let finalEndpoint = endpoint;
    if (finalEndpoint.startsWith('/nutribot/') && uid) {
      finalEndpoint += finalEndpoint.includes('?')
        ? `&userId=${encodeURIComponent(uid)}`
        : `?userId=${encodeURIComponent(uid)}`;
    }

    console.log('🌍 API Request:', `${API_BASE_URL}${finalEndpoint}`);
    console.log('📦 Headers:', headers);
    if (options.body) {
      try {
        console.log('📝 Request Body:', JSON.parse(options.body as string));
      } catch (e) {
        console.error('⚠️ Failed to parse request body:', {
          body: options.body,
          error: (e as Error).message,
          timestamp: new Date().toISOString(),
        });
        throw new Error('Invalid request body format');
      }
    }

    const config: RequestInit = {
      ...options,
      headers,
      // Ensure cookie-based sessions are sent (for nutribot.ch prod)
      credentials: 'include',
    };

    try {
      const response = await fetch(`${API_BASE_URL}${finalEndpoint}`, config);

      console.log('📡 Response status:', response.status);

      const text = await response.text();
      console.log('📄 Raw response text:', text);

      let data;
      try {
        data = JSON.parse(text);
      } catch {
        console.warn('⚠️ Failed to parse response as JSON:', text);
        data = text;
      }

      if (!response.ok) {
        console.error('❌ API error response:', {
          status: response.status,
          statusText: response.statusText,
          data,
          endpoint,
          timestamp: new Date().toISOString(),
        });
        const errorMessage = data?.error || data?.details || data?.message || `API request failed with status ${response.status}`;
        throw new Error(errorMessage);
      }

      console.log('✅ API success response:', data);
      return data;
    } catch (error: any) {
      console.error('🔥 API request failed:', {
        error: error.message,
        endpoint,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }

  static async checkAuth(): Promise<{ isAuthenticated: boolean; user: User | null }> {
    // Proactively detect admin tokens and avoid calling /user/profile, which returns 401 for admins
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const parts = token.split('.');
        if (parts.length === 3) {
          const payloadJson = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
          const payload = JSON.parse(payloadJson);
          const email: string | undefined = payload.email || payload.userEmail || payload.sub;
          const id: string | undefined = payload.userId || payload.id || payload.sub;
          const rawRole: string | undefined = payload.role;
          const inferredAdmin = !!(email && /admin|nutribot/i.test(String(email)));
          const role = rawRole === 'admin' || inferredAdmin ? 'admin' : 'user';
          if (role === 'admin') {
             const adminUser: any = {
               id: id || 'local-admin',
               email: email || 'admin@local',
               name: payload.name || 'Admin',
               plan: 'vip',
               isAuthenticated: true,
               role: 'admin',
               isAdmin: true,
               mealPlan: null,
             };
             return { isAuthenticated: true, user: adminUser };
           }
        }
      } catch (e) {
        console.warn('Admin pre-check decode failed:', (e as Error).message);
      }
    }

    try {
      // Canonical auth check for regular users
      const response = await this.request('/user/profile', { method: 'GET' });
      const userObj = response?.user;
      const isAuthenticated = !!userObj;
      return {
        isAuthenticated,
        user: userObj ? {
          ...userObj,
          mealPlan: userObj.mealPlan ?? null,
          isAuthenticated,
        } : null,
      };
    } catch (error: any) {
      console.error('Auth check failed:', {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      });
      // Fallback: if a token exists, decode minimal fields to populate a basic user
      if (token) {
        try {
          const parts = token.split('.');
          const payloadJson = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
          const payload = JSON.parse(payloadJson);
          const email: string | undefined = payload.email || payload.userEmail || payload.sub;
          const id: string | undefined = payload.userId || payload.id || payload.sub;
          const rawRole: string | undefined = payload.role;
          const inferredAdmin = !!(email && /admin|nutribot/i.test(String(email)));
          const role = rawRole === 'admin' || inferredAdmin ? 'admin' : 'user';
          const tier = payload.tier || 'vip';
          const fauxUser: any = {
            id: id || 'local-user',
            email: email || 'user@local',
            name: payload.name || 'User',
            plan: tier && String(tier).toLowerCase().includes('vip') ? 'vip' : 'basic',
            isAuthenticated: true,
            role,
            isAdmin: role === 'admin',
            mealPlan: null,
          };
          return { isAuthenticated: true, user: fauxUser };
        } catch (decodeErr) {
          console.warn('Token decode fallback failed:', (decodeErr as Error).message);
          return { isAuthenticated: true, user: { id: 'local-user', email: 'user@local', name: 'User', plan: 'basic', isAuthenticated: true } as any };
        }
      }
      return { isAuthenticated: false, user: null };
    }
  }

  static async getMealPlan(): Promise<{ mealPlan: MealPlan | null }> {
    try {
      const response = await this.request('/user/meal-plan');
      if (response.mealPlan) {
        try {
          // Client-side normalization to handle partial/invalid shapes
          const normalizeClientMealPlan = (raw: any): MealPlan => {
            const fallbackMeals = () => ([
              { name: 'Oatmeal with berries', foods: [], calories: 350, time: '08:00' },
              { name: 'Chicken salad', foods: [], calories: 500, time: '12:30' },
              { name: 'Grilled fish with veggies', foods: [], calories: 600, time: '19:00' },
            ]);

            const normalizeMeals = (inMeals: any): Meal[] => {
              if (!Array.isArray(inMeals)) {
                if (typeof inMeals === 'string' && inMeals.trim()) {
                  return [{ name: inMeals.trim(), foods: [], calories: 0, time: '' }];
                }
                return fallbackMeals();
              }
              const out = inMeals
                .map((m: any) => {
                  if (typeof m === 'string') {
                    const nm = m.trim();
                    if (nm) return { name: nm, foods: [], calories: 0, time: '' } as Meal;
                    return null;
                  }
                  if (!m || typeof m.name !== 'string' || m.name.trim() === '') {
                    return null;
                  }
                  return {
                    name: m.name,
                    foods: Array.isArray(m.foods) ? m.foods : [],
                    calories: typeof m.calories === 'number' ? m.calories : 0,
                    time: typeof m.time === 'string' ? m.time : '',
                  } as Meal;
                })
                .filter(Boolean) as Meal[];
              return out.length > 0 ? out : fallbackMeals();
            };

            const baseDays: DayPlan[] = Array.isArray(raw?.days)
              ? raw.days.map((d: any, idx: number) => ({
                  day: typeof d?.day === 'string' && d.day.trim().length > 0 ? d.day : `Tag ${idx + 1}`,
                  meals: normalizeMeals(d?.meals),
                }))
              : [];

            const days: DayPlan[] = (() => {
              if (baseDays.length === 0) {
                return Array.from({ length: 30 }).map((_, i) => ({ day: `Tag ${i + 1}`, meals: fallbackMeals() }));
              }
              const out: DayPlan[] = [...baseDays];
              while (out.length < 30) {
                const src = baseDays[out.length % baseDays.length];
                const clone: DayPlan = {
                  day: `Tag ${out.length + 1}`,
                  meals: src?.meals ? JSON.parse(JSON.stringify(src.meals)) : fallbackMeals(),
                };
                out.push(clone);
              }
              return out.slice(0, 30);
            })();

            const totalCaloriesProvided = typeof raw?.totalCalories === 'number' ? raw.totalCalories : 0;
            const computedTotal = days.reduce(
              (sum: number, dy: DayPlan) => sum + dy.meals.reduce((s: number, m: Meal) => s + (typeof m.calories === 'number' ? m.calories : 0), 0),
              0
            );

            return {
              id: raw?.id,
              generatedAt: raw?.generatedAt || new Date().toISOString(),
              days,
              text: typeof raw?.text === 'string' ? raw.text : '',
              totalCalories: totalCaloriesProvided > 0 ? totalCaloriesProvided : computedTotal,
              shoppingList: Array.isArray(raw?.shoppingList) ? raw.shoppingList : [],
              dietType: raw?.dietType,
            };
          };

          // Accept objects and strings; normalize either way
          if (typeof response.mealPlan === 'object') {
            const normalized = normalizeClientMealPlan(response.mealPlan);
            return { mealPlan: normalized };
          }
          if (typeof response.mealPlan === 'string') {
            const trimmed = response.mealPlan.trim();
            if (!trimmed || trimmed === '[object Object]') {
              console.warn('Invalid meal plan data detected:', { mealPlan: response.mealPlan, timestamp: new Date().toISOString() });
              return { mealPlan: null };
            }
            const parsed = JSON.parse(trimmed);
            const normalized = normalizeClientMealPlan(parsed);
            return { mealPlan: normalized };
          }
          console.warn('Unexpected meal plan type:', { type: typeof response.mealPlan, mealPlan: response.mealPlan, timestamp: new Date().toISOString() });
          return { mealPlan: null };
        } catch (parseError: any) {
          console.error('Failed to parse meal plan:', {
            error: parseError.message,
            stack: parseError.stack,
            rawData: response.mealPlan,
            timestamp: new Date().toISOString(),
          });
          return { mealPlan: null };
        }
      }
      console.log('No meal plan found in response:', { response, timestamp: new Date().toISOString() });
      return { mealPlan: null };
    } catch (error: any) {
      console.error('Error fetching meal plan:', {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      });
      throw new Error(`Failed to fetch meal plan: ${error.message}`);
    }
  }

  static async register(userData: { email: string; name: string; password: string; tier?: string; recaptchaToken?: string }): Promise<{ user: User; token: string }> {
    const response = await this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    if (response.token) {
      localStorage.setItem('token', response.token);
    }
    return response;
  }

  static async login(credentials: { email: string; password: string; recaptchaToken?: string }): Promise<{ user: User; token: string }> {
    const result = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    if (result.token) {
      localStorage.setItem('token', result.token);
    }
    return result;
  }

  static async adminLogin(credentials: { email: string; password: string }): Promise<{ token: string }> {
    const result = await this.request('/admin/admin-login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    if (result.token) {
      localStorage.setItem('token', result.token);
    }
    return result;
  }

  static async logout(): Promise<void> {
    await this.request('/auth/logout', { method: 'POST' });
    localStorage.removeItem('token');
  }

  static async getProfile(): Promise<{ user: User }> {
    return this.request('/user/profile');
  }

  static async updateProfile(profileData: { name: string }): Promise<{ user: User }> {
    return this.request('/user/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  static async getSubscription(): Promise<any> {
    return this.request('/user/subscription');
  }

  static async cancelSubscription(): Promise<void> {
    await this.request('/user/subscription', { method: 'DELETE' });
  }

  static async getStripePrices(): Promise<{ plans: any; updatedAt: string }> {
    return this.request('/stripe/prices');
  }

  // Restored methods used by NutriBot with robust fallbacks
  static async refineMealPlanQuestion(request: RefinedQuestionRequest): Promise<RefinedQuestionResponse> {
    try {
      const response = await this.request('/nutribot/refine-question', {
        method: 'POST',
        body: JSON.stringify(request),
      });
      return {
        refinedQuestion: response.refinedQuestion || this.getFallbackQuestion(request.questionType || 'first_question'),
        options: response.options || [],
        shouldProceed: response.shouldProceed !== false,
        message: response.message,
      };
    } catch (error: any) {
      console.error('Error refining meal plan question:', error);
      return {
        refinedQuestion: this.getFallbackQuestion(request.questionType || 'first_question'),
        options: [],
        shouldProceed: true,
        message: 'fallback',
      };
    }
  }

  static async validateDataCompleteness(params: { collectedData: Record<string, any>; criticalFields: string[] }): Promise<{ missingFields: string[] }> {
    try {
      const response = await this.request('/nutribot/validate-completeness', {
        method: 'POST',
        body: JSON.stringify(params),
      });
      return { missingFields: response.missingFields || [] };
    } catch (error: any) {
      console.warn('Completeness validation failed, using basic check');
      const { collectedData, criticalFields } = params;
      const missing = criticalFields.filter((field) => !collectedData[field] && !collectedData[`step_${field}`]);
      return { missingFields: missing };
    }
  }

  static async processMealPlanAnswer(request: AnswerProcessingRequest): Promise<AnswerProcessingResponse> {
    try {
      const response = await this.request('/nutribot/process-answer', {
        method: 'POST',
        body: JSON.stringify(request),
      });
      return {
        isValid: !!response.isValid,
        processedValue: response.processedValue,
        shouldContinue: response.shouldContinue !== false,
        nextStep: response.nextStep,
        message: response.message,
        errorMessage: response.errorMessage,
        isComplete: !!response.isComplete,
      };
    } catch (error: any) {
      console.error('Error processing meal plan answer:', error);
      const val = (request.userAnswer || '').trim();
      const isValid = val.length > 0;
      return {
        isValid,
        processedValue: isValid ? val : undefined,
        shouldContinue: true,
        nextStep: undefined,
        message: isValid ? undefined : 'Ungültige Antwort',
        errorMessage: isValid ? undefined : 'Bitte geben Sie eine gültige Antwort ein.',
        isComplete: false,
      };
    }
  }

  static async getLandingMetrics(): Promise<{ socialCount: number; rating: number; updatedAt: string }> {
    return this.request('/public/landing-metrics');
  }

  static async sendEmailOtp(email: string): Promise<{ message: string; delivery: string; requestId: string }> {
    return this.request('/auth/otp-send', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  static async verifyEmailOtp(email: string, code: string): Promise<{ verified: boolean }> {
    return this.request('/auth/otp-verify', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
  }

















  /* duplicate refineMealPlanQuestion removed */

  /* duplicate processMealPlanAnswer removed */

  static async generateMealPlanViaChatGPT(request: MealPlanGenerationRequest): Promise<MealPlanResponse> {
    try {
      const response = await this.request('/nutribot/generate-meal-plan-chatgpt', {
        method: 'POST',
        body: JSON.stringify(request),
      });
      return response;
    } catch (error: any) {
      console.error('Error generating meal plan via ChatGPT:', error);
      throw error;
    }
  }

  static async getComprehensiveConversationFlow(): Promise<{ totalSteps: number; flowType: string }> {
    try {
      const response = await this.request('/nutribot/conversation-flow');
      return {
        totalSteps: response.totalSteps || 12,
        flowType: response.flowType || 'comprehensive',
      };
    } catch (error) {
      console.error('Error getting comprehensive conversation flow:', error);
      return { totalSteps: 12, flowType: 'comprehensive' };
    }
  }

  static async getConversationFlow(): Promise<{ totalSteps: number; flowType: string }> {
    try {
      const response = await this.request('/nutribot/conversation-flow');
      return response;
    } catch (error) {
      console.error('Error getting conversation flow:', error);
      return { totalSteps: 12, flowType: 'standard' };
    }
  }

  static async validateMealPlanData(request: ValidationRequest): Promise<ValidationResponse> {
    try {
      const response = await this.request('/nutribot/validate-data', {
        method: 'POST',
        body: JSON.stringify(request),
      });
      return {
        isComplete: !!response.isComplete,
        missingFields: response.missingFields || [],
        suggestedNextStep: response.suggestedNextStep,
      };
    } catch (error) {
      console.error('Error validating meal plan data:', error);
      const required = ['age','gender','height','weight','activityLevel','goal','mealsPerDay'];
      const dataKeys = Object.keys(request.conversationData || {});
      const missing = required.filter((k) => !dataKeys.includes(k));
      return {
        isComplete: missing.length === 0,
        missingFields: missing,
        suggestedNextStep: missing[0] ? `Bitte teilen Sie mir Ihre ${missing[0]} mit.` : undefined,
      };
    }
  }

  static async generateMealPlan(intakeData: Partial<IntakeFormData>): Promise<{ mealPlan: MealPlan }> {
    try {
      console.log('🔄 Calling generateMealPlan API with data:', intakeData);
      
      const response = await this.request('/user/meal-plan', {
        method: 'POST',
        body: JSON.stringify({
          message: 'generate-plan',
          formData: intakeData
        }),
      });
      
      console.log('📥 Raw API response:', response);
      
      if (response.mealPlan) {
        try {
          await this.saveMealPlan(response.mealPlan);
          console.log('💾 Meal plan saved after generation');
        } catch (saveError: any) {
          console.error('⚠️ Failed to save meal plan after generation:', saveError);
          // Don't throw - we still have the meal plan
        }
      }
      
      return response;
    } catch (error: any) {
      console.error('❌ generateMealPlan error details:', {
        message: error.message,
        response: error.response,
        status: error.status,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      });
      throw new Error(`Failed to generate meal plan: ${error.message}`);
    }
  }

  static async saveMealPlan(mealPlan: MealPlan): Promise<void> {
    try {
      const response = await this.request('/user/meal-plan/save', {
        method: 'POST',
        body: JSON.stringify({ mealPlan }),
      });
      console.log('Meal plan saved successfully:', { mealPlanId: mealPlan.id, response, timestamp: new Date().toISOString() });
    } catch (error: any) {
      console.error('Error saving meal plan:', {
        error: error.message,
        stack: error.stack,
        mealPlanId: mealPlan.id,
        timestamp: new Date().toISOString(),
      });
      throw new Error(`Failed to save meal plan: ${error.message}`);
    }
  }

  static async sendMealPlanEmail(mealPlan?: MealPlan): Promise<void> {
    try {
      // Derive recipient from stored user if available
      let recipient: string | undefined;
      try {
        const raw = localStorage.getItem('user');
        if (raw) recipient = JSON.parse(raw)?.email;
      } catch {}

      // Fallback: attempt profile fetch if not found
      if (!recipient) {
        try {
          const prof = await this.getProfile();
          recipient = prof?.user?.email;
        } catch {}
      }

      // Build a human-readable message string for the email endpoint
      const message = (() => {
        try {
          if (mealPlan) {
            const lines: string[] = [];
            lines.push('Personalized Meal Plan');
            if ((mealPlan as any).dietType) lines.push(`Goal: ${(mealPlan as any).dietType}`);
            if ((mealPlan as any).totalCalories) lines.push(`Calories/Day: ~${(mealPlan as any).totalCalories}`);
            const days = (mealPlan as any).days || [];
            days.forEach((d: any) => {
              lines.push(`\n${d.day || 'Day'}:`);
              (d.meals || []).forEach((m: any) => {
                const parts: string[] = [];
                if (m.time) parts.push(`[${m.time}]`);
                parts.push(m.name || 'Meal');
                if (m.calories) parts.push(`${m.calories} kcal`);
                lines.push(` • ${parts.join(' ')}`);
              });
            });
            const list = (mealPlan as any).shoppingList || [];
            if (Array.isArray(list) && list.length) {
              lines.push('\nShopping List:');
              list.forEach((it: any) => {
                const name = typeof it === 'string' ? it : (it?.item || it?.name || it?.product || it?.food || it?.title || it?.label || '');
                if (!name) return;
                const qty = typeof it === 'object' ? (it?.quantity || it?.qty || it?.amount || '') : '';
                lines.push(` - ${name}${qty ? ` (${qty})` : ''}`);
              });
            }
            return lines.join('\n');
          }
        } catch {}
        return 'Please send my latest meal plan via email.';
      })();

      // Post to NutriBot email endpoint (expects a string message)
      await this.request('/nutribot/email-plan', {
        method: 'POST',
        body: JSON.stringify({
          to: recipient,
          message
        }),
      });
      console.log('Meal plan email sent successfully:', { mealPlanId: mealPlan?.id, to: recipient, timestamp: new Date().toISOString() });
    } catch (error: any) {
      console.error('Error sending meal plan email:', {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      });
      // Do not throw to avoid blocking UI
    }
  }

  static async downloadMealPlanPDF(): Promise<{ downloadUrl: string }> {
    try {
      const token = localStorage.getItem('token');
      const headers: HeadersInit = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Accept': 'application/json, application/pdf;q=0.9'
      };

      console.log('🌍 PDF Download Request:', `${API_BASE_URL}/user/meal-plan/pdf`);
      const response = await fetch(`${API_BASE_URL}/user/meal-plan/pdf`, {
        method: 'GET',
        headers,
      });

      console.log('📡 PDF Response status:', response.status);
      const contentType = (response.headers.get('content-type') || '').toLowerCase();
      console.log('📄 PDF Content-Type:', contentType);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ PDF download error response:', {
          status: response.status,
          statusText: response.statusText,
          errorText,
          timestamp: new Date().toISOString(),
        });
        throw new Error(`PDF download failed: ${errorText}`);
      }

      // Prefer JSON URL when available (backend enhanced route)
      if (contentType.includes('application/json')) {
        const data = await response.json().catch(() => ({} as any));
        const directUrl: string = data?.downloadUrl || data?.url || '';
        if (!directUrl) throw new Error('No downloadUrl in JSON response');
        console.log('✅ Received direct PDF URL from backend:', directUrl);
        return { downloadUrl: directUrl };
      }

      // Fallback to blob streaming
      const pdfBlob = await response.blob();
      const downloadUrl = URL.createObjectURL(pdfBlob);
      console.log('✅ PDF blob created successfully, size:', pdfBlob.size, 'bytes');
      return { downloadUrl };
    } catch (error: any) {
      console.error('Error downloading PDF:', {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      });
      throw new Error(`Failed to download PDF: ${error.message}`);
    }
  }

  static async generateEnhancedMealPlanPDF(mealPlan: ExtendedMealPlan, user: Pick<User, 'id' | 'name' | 'email'>, store?: string): Promise<{ downloadUrl: string }> {
    try {
      console.log('📄 Generating enhanced meal plan PDF...');
      
      const days = Array.isArray(mealPlan?.days) ? mealPlan.days : [];
      // Ensure days have snacks/meals arrays
      const normalizedDays = days.map((d: any, idx: number) => ({
        day: d?.day || `Day ${idx + 1}`,
        meals: Array.isArray(d?.meals) ? d.meals : [],
        snacks: Array.isArray((d as any)?.snacks) ? (d as any).snacks : [],
      }));
      const shoppingList = Array.isArray((mealPlan as any)?.shoppingList) ? (mealPlan as any).shoppingList : [];

      const response = await this.request('/nutribot/generate-pdf', {
        method: 'POST',
        body: JSON.stringify({
          mealPlan: {
            ...mealPlan,
            days: normalizedDays,
            shoppingList,
            userInfo: {
              name: user.name,
              goal: mealPlan.dietType,
              targetCalories: mealPlan.totalCalories?.toString?.() || String(mealPlan.totalCalories || '')
            }
          },
          user: {
            id: user.id,
            name: user.name,
            email: user.email
          },
          store: store || 'vip-dashboard',
          // Feature flags for backend formatting
          features: { includeShoppingList: true, includeRecipes: true },
          // Add explicit endpoint hint in body, per integration request
          pdfEndpoint: '/nutribot/generate-pdf'
        })
      });

      if (!response?.success && !response?.downloadUrl) {
        throw new Error(response?.error || 'PDF generation failed');
      }

      return { downloadUrl: response.downloadUrl };
    } catch (error: any) {
      console.error('Error generating enhanced PDF:', {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      });
      throw new Error(`Failed to generate enhanced PDF: ${error.message}`);
    }
  };


  static async chatWithNutriBot(payload: ChatRequest): Promise<ChatResponse> {
    const { message, sessionId, language } = payload;
    try {
      const data = await this.request('/nutribot/chat', {
        method: 'POST',
        body: JSON.stringify({ message, sessionId, language, intentHint: (payload as any).intentHint }),
      });

      if (data?.error && data?.status === 401) throw new Error('AUTHENTICATION_REQUIRED');
      return data as ChatResponse;
    } catch (error: any) {
      if (error?.message?.includes('upgrade')) {
        throw new Error('Bitte upgraden Sie auf VIP, um NutriBot unbegrenzt zu nutzen.');
      }
      if (error?.message?.includes('prompt limit')) {
        throw new Error('Sie haben Ihr kostenloses Kontingent erreicht. Bitte upgraden.');
      }
      throw error;
    }
  }
  static async chatWithNutriBotStream(payload: ChatRequest, onToken: (token: string) => void): Promise<{ finalText: string; meta?: any }> {
    const token = localStorage.getItem('token');
    // Ensure we always pass a userId to avoid 401 and enable SSE path
    const uid = ApiService.getUserIdFromToken() || localStorage.getItem('userId') || 'diagnostic';
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      // Avoid x-user-id header to prevent CORS; use query param instead
    };
    const body = JSON.stringify({ ...payload, stream: true, intentHint: (payload as any).intentHint });
    const qs = uid ? `?userId=${encodeURIComponent(uid)}` : '';

    const response = await fetch(`${API_BASE_URL}/nutribot/chat${qs}`,
    {
      method: 'POST',
      headers,
      body,
    });

    if (!response.ok) {
      const text = await response.text();
      const err: any = new Error(text || `Streaming request failed with status ${response.status}`);
      err.status = response.status;
      throw err;
    }

    const reader = response.body?.getReader();
    if (!reader) return { finalText: '' };

    const decoder = new TextDecoder();
    let buffer = '';
    let finalText = '';
    let meta: any = undefined;

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let idx = buffer.indexOf('\n\n');
      while (idx !== -1) {
        const chunk = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);

        let eventType = 'message';
        let dataPayload = '';
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('event:')) eventType = line.slice(6).trim();
          else if (line.startsWith('data:')) dataPayload += line.slice(5).trim();
        }

        if (dataPayload) {
          try {
            const parsed = JSON.parse(dataPayload);
            if (eventType === 'done') {
              if (parsed?.text) finalText = parsed.text;
              meta = parsed;
            } else if (parsed?.token) {
              onToken(parsed.token);
            }
          } catch (e) {
            // Ignore parse errors for streaming noise
          }
        }

        idx = buffer.indexOf('\n\n');
      }
    }

    return { finalText, meta };
  }
  static async generateMealPlanViaNutriBot(formData: IntakeFormData): Promise<MealPlanResponse> {
    try {
      const response = await this.request('/nutribot/generate-plan', {
        method: 'POST',
        body: JSON.stringify({ formData }),
      });
      return response;
    } catch (error: any) {
      console.error('Error generating meal plan via NutriBot:', {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }

  static async getChatHistory(sessionId?: string): Promise<any> {
    const endpoint = sessionId ? `/nutribot/history?sessionId=${sessionId}` : '/nutribot/history';
    return this.request(endpoint);
  }

  static async createCheckoutSession(priceId: string, successUrl: string, cancelUrl: string, promotionCode?: string): Promise<any> {
    return this.request('/stripe/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify({ priceId, successUrl, cancelUrl, promotionCode }),
    });
  }

  static async getUserStats(): Promise<{ stats: UserStats }> {
    return this.request('/user/stats');
  }

  static async updateNextUpdate(): Promise<void> {
    await this.request('/user/update-next-update', { method: 'POST' });
  }

  static async updatePassword(passwordData: { currentPassword: string; newPassword: string }): Promise<void> {
    return this.request('/auth/update-password', {
      method: 'POST',
      body: JSON.stringify(passwordData),
    });
  }

  static async getNotificationSettings(): Promise<{[key: string]: boolean}> {
    return this.request('/user/notification-settings', {
      method: 'GET',
    });
  }

  static async updateNotificationSetting(settingId: string, enabled: boolean): Promise<void> {
    return this.request('/user/notification-settings', {
      method: 'PUT',
      body: JSON.stringify({ settingId, enabled }),
    });
  }

  static async deleteAccount(): Promise<void> {
    return this.request('/user/delete-account', {
      method: 'DELETE',
    });
  }

  static async getBillingHistory(): Promise<{ transactions: BillingTransaction[] }> {
    return this.request('/user/billing-history', {
      method: 'GET',
    });
  }

  static async testConnection(): Promise<any> {
    return this.request('/auth/test');
  }

  static async getAdminStats(): Promise<{ stats: AdminStats }> {
    const data = await this.request('/admin/stats');
    const stats = data?.stats ?? data;
    if (!stats || typeof stats !== 'object') {
      throw new Error('Invalid admin stats response');
    }

    const totalUsers = Number(stats.totalUsers ?? 0);
    const vipUsers = Number(stats.vipUsers ?? 0);
    const basicUsers = Number(stats.basicUsers ?? 0);
    const activeSubscriptions = Number(stats.activeSubscriptions ?? 0);
    const totalRevenue = Number(stats.totalRevenue ?? 0);
    const recentSignups = Number(stats.recentSignups ?? 0);
    const monthlyRevenue = Array.isArray(stats.monthlyRevenue) ? stats.monthlyRevenue : [];
    const userGrowth = Array.isArray(stats.userGrowth) ? stats.userGrowth : [];
    const planDistributionRaw = Array.isArray(stats.planDistribution) ? stats.planDistribution : [];

    // Fallback: compute simple plan distribution if backend doesn't provide it
    const planDistribution = planDistributionRaw.length > 0
      ? planDistributionRaw
      : [
          { tier: 'vip', count: vipUsers },
          { tier: 'basic', count: basicUsers },
          { tier: 'guest', count: Math.max(0, totalUsers - vipUsers - basicUsers) },
        ];

    return {
      stats: {
        totalUsers,
        vipUsers,
        basicUsers,
        activeSubscriptions,
        totalRevenue,
        recentSignups,
        monthlyRevenue,
        userGrowth,
        planDistribution,
      },
    };
  }

  static async sendBulkEmail(emailType: string, subject: string, content: string): Promise<{
    success: number;
    failed: number;
    totalRecipients: number;
    message: string;
  }> {
    return this.request('/admin/send-bulk-email', {
      method: 'POST',
      body: JSON.stringify({
        emailType,
        subject,
        content
      }),
    });
  }

  static async getNotificationStats(): Promise<{
    totalUsers: number;
    nutribotNewsSubscribers: number;
    marketingSubscribers: number;
    mealPlanSubscribers: number;
    accountUpdateSubscribers: number;
  }> {
    return this.request('/admin/notification-stats');
  }

  static async getAdminUsers(): Promise<{ users: AdminUser[] }> {
    return this.request('/admin/users');
  }

  static async adminUserAction(userId: string, action: 'ban' | 'activate' | 'delete'): Promise<void> {
    return this.request('/admin/users', {
      method: 'POST',
      body: JSON.stringify({ userId, action }),
    });
  }

  static async updateAdminUser(userId: number, updates: Partial<AdminUser & { password?: string; tier?: string; subscriptionEnd?: string; status?: string; name?: string; email?: string; durationDays?: number }>): Promise<{ success: boolean; message?: string }> {
    console.log('AdminService: updateAdminUser called', { userId, updates });
    return this.request(`/admin/users`, {
      method: 'PUT',
      body: JSON.stringify({ userId, updates }),
    });
  }

  static async adminCreateUser(params: { email: string; name: string; password?: string; tier?: string; durationDays?: number }): Promise<any> {
    console.log('AdminService: adminCreateUser called', params);
    return this.request(`/admin/create-user`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  static async adminCreatePromoCode(params: { code: string; discountType: 'percent' | 'amount'; value: number; maxRedemptions?: number; expiresAt?: string; influencerName?: string; landingPath?: string }): Promise<{ success: boolean; promotionCode: any; couponId?: string }> {
    return this.request('/stripe/create-promo-code', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  static async getPromoAnalytics(): Promise<{ codes: Array<{ code: string; active: boolean; couponId?: string; timesRedeemed: number; maxRedemptions?: number; expiresAt?: number; metadata?: Record<string, string> }> }> {
    return this.request('/stripe/promo-analytics');
  }


  // Get all meal plans for a user
  static async getAllMealPlans(): Promise<{ mealPlans: MealPlan[] }> {
    try {
      const response = await this.request('/user/meal-plan?all=true');
      return { mealPlans: response.mealPlans || [] };
    } catch (error: any) {
      console.error('Error fetching all meal plans:', {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      });
      return { mealPlans: [] };
    }
  }

  // Delete a specific meal plan
  static async deleteMealPlan(mealPlanId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await this.request('/user/meal-plan', {
        method: 'DELETE',
        body: JSON.stringify({ mealPlanId }),
      });
      return response;
    } catch (error: any) {
      console.error('Error deleting meal plan:', {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }

  // Fetch the current (active) meal plan
  static async getCurrentMealPlan(): Promise<{ mealPlan: ExtendedMealPlan | MealPlan | null }> {
    try {
      const response = await this.request('/user/meal-plan');
      const raw = response.mealPlan;
      if (!raw) return { mealPlan: null };

      // Local normalization mirror of getMealPlan to ensure 30 days and valid meals
      const fallbackMeals = () => ([
        { name: 'Oatmeal with berries', foods: [], calories: 350, time: '08:00' },
        { name: 'Chicken salad', foods: [], calories: 500, time: '12:30' },
        { name: 'Grilled fish with veggies', foods: [], calories: 600, time: '19:00' },
      ]);

      const getPrefs = () => {
        try {
          const u = JSON.parse(localStorage.getItem('user') || '{}');
          const allergies = Array.isArray(u?.allergies) ? u.allergies : [];
          const disliked = Array.isArray(u?.dislikedFoods) ? u.dislikedFoods : [];
          const liked = Array.isArray(u?.likedFoods) ? u.likedFoods : [];
          const restrictions = Array.isArray(u?.dietaryRestrictions) ? u.dietaryRestrictions : [];
          return { allergies, disliked, liked, restrictions };
        } catch {
          return { allergies: [], disliked: [], liked: [], restrictions: [] };
        }
      };

      const sanitizeFoods = (inFoods: any): string[] => {
        const { allergies, disliked, restrictions } = getPrefs();
        let arr: string[] = [];
        if (Array.isArray(inFoods)) arr = inFoods as string[];
        else if (typeof inFoods === 'string') arr = [inFoods];

        const normList = (list: any[]): string[] => list
          .filter((x) => typeof x === 'string')
          .map((x) => x.trim())
          .filter((x) => x.length > 0);

        const a = normList(allergies);
        const d = normList(disliked);
        const r = normList(restrictions);

        const shouldExclude = (item: string) => {
          const low = item.toLowerCase();
          const hits = (set: string[]) => set.some((t) => {
            const tt = t.toLowerCase();
            if (tt.length >= 3) return low.includes(tt);
            return low === tt;
          });
          return hits(a) || hits(d) || hits(r);
        };

        const out = normList(arr)
          .filter((f) => !shouldExclude(f));

        const seen = new Set<string>();
        const dedup = out.filter((f) => {
          const key = f.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        return dedup;
      };

      const baseDays: DayPlan[] = Array.isArray(raw?.days)
        ? raw.days.map((d: any, idx: number) => ({
            day: typeof d?.day === 'string' && d.day.trim().length > 0 ? d.day : `Tag ${idx + 1}`,
            meals: Array.isArray(d?.meals)
              ? d.meals.map((m: any) => ({
                  name: typeof m?.name === 'string' ? m.name : 'Meal',
                  foods: sanitizeFoods(m?.foods),
                  calories: typeof m?.calories === 'number' ? m.calories : 0,
                  time: typeof m?.time === 'string' ? m.time : '',
                }))
              : fallbackMeals(),
          }))
        : [];

      const days: DayPlan[] = (() => {
        if (baseDays.length === 0) {
          return Array.from({ length: 30 }).map((_, i) => ({ day: `Tag ${i + 1}`, meals: fallbackMeals() }));
        }
        const out: DayPlan[] = [...baseDays];
        while (out.length < 30) {
          const src = baseDays[out.length % baseDays.length];
          const clone: DayPlan = {
            day: `Tag ${out.length + 1}`,
            meals: src?.meals ? JSON.parse(JSON.stringify(src.meals)) : fallbackMeals(),
          };
          out.push(clone);
        }
        return out.slice(0, 30);
      })();
      const totalCaloriesProvided = typeof raw?.totalCalories === 'number' ? raw.totalCalories : 0;
      const computedTotal = days.reduce(
        (sum: number, dy: DayPlan) => sum + dy.meals.reduce((s: number, m: Meal) => s + (typeof m.calories === 'number' ? m.calories : 0), 0),
        0
      );
      const normalized: MealPlan = {
        id: raw?.id,
        generatedAt: raw?.generatedAt || new Date().toISOString(),
        days,
        text: typeof raw?.text === 'string' ? raw.text : '',
        totalCalories: totalCaloriesProvided > 0 ? totalCaloriesProvided : computedTotal,
        shoppingList: Array.isArray(raw?.shoppingList) ? raw.shoppingList : [],
        dietType: raw?.dietType,
      };
      return { mealPlan: normalized };
    } catch (error: any) {
      console.error('Error fetching current meal plan:', {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      });
      return { mealPlan: null };
    }
  }

  // Generate next week's plan (server-side)
  static async generateNextWeekPlan(): Promise<{ success: boolean; mealPlan?: ExtendedMealPlan; message?: string }> {
    try {
      const response = await this.request('/user/meal-plan/next-week', {
        method: 'POST',
      });
      return response;
    } catch (error: any) {
      console.error('Error generating next week plan:', {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }

  // Enhanced meal plan with recipes
  static async generateMealPlanWithRecipes(formData: IntakeFormData): Promise<{ success: boolean; mealPlan: ExtendedMealPlan; message?: string }> {
    try {
      const response = await this.request('/nutribot/generate-enhanced-plan', {
        method: 'POST',
        body: JSON.stringify({ formData }),
      });
      return response;
    } catch (error: any) {
      console.error('Error generating meal plan with recipes:', {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }

  // === Added for password reset ===
  static async requestPasswordReset(email: string): Promise<{ message: string }> {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  static async resetPassword(token: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }


  private static getFallbackQuestion(questionType: string): string {
    const fallbackQuestions: Record<string, string> = {
      'first_question': 'Lassen Sie uns Ihren personalisierten Ernährungsplan erstellen! 🌟 Wie alt sind Sie?',
      'age': 'Um Ihnen den perfekten Ernährungsplan zu erstellen, lassen Sie uns mit ein paar Fragen beginnen! 🌟 Wie alt sind Sie?',
      'gender': 'Danke! Was ist Ihr Geschlecht?',
      'height': 'Perfekt! Wie groß sind Sie? (in cm)',
      'weight': 'Großartig! Wie viel wiegen Sie aktuell? (in kg)',
      'activityLevel': 'Verstanden! Wie aktiv sind Sie im Alltag?',
      'goal': 'Was möchten Sie mit dem Ernährungsplan erreichen?',
      'targetWeight': 'Was ist Ihr Wunschgewicht?',
      'timeframe': 'In welchem Zeitraum möchten Sie Ihr Ziel erreichen?',
      'mealsPerDay': 'Wie viele Hauptmahlzeiten pro Tag bevorzugen Sie?',
      'snacksPerDay': 'Und wie viele Snacks pro Tag?',
      'dietaryRestrictions': 'Haben Sie besondere Ernährungsweisen oder Einschränkungen?',
      'allergies': 'Gibt es Lebensmittelallergien oder Unverträglichkeiten?',
      'dislikedFoods': 'Welche Lebensmittel mögen Sie gar nicht?',
      'likedFoods': 'Und welche Lebensmittel lieben Sie besonders?',
      'targetCalories': 'Haben Sie ein Kalorienziel pro Tag?',
      'cookingTime': 'Wie viel Zeit haben Sie typically zum Kochen?',
      'budget': 'Welches Budget haben Sie für Lebensmittel?',
    };
    return fallbackQuestions[questionType] || 'Könnten Sie mir das bitte mitteilen?';
  }

  static async getNutribotUpdates(): Promise<{ updates: Array<{ id: string; date: string; title: string; description: string; highlights?: string[] }> }> {
    return this.request('/nutribot/updates');
  }

}

export default ApiService;